import type { MapDispatchToPropsFunction, MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import {
  batchFormSelectToggled,
  batchFormsDeleted,
  batchFormsDeleteRequested,
  batchQuoteExpired,
  batchQuotePushBackCancelRequested,
} from 'state/batchForm/batchForm.action';
import {
  batchFormRestoreSessionThunk,
  batchFormsDeletedThunk,
  defaultBatchFormRequestedThunk,
} from 'state/batchForm/batchForm.thunks';
import { FormStatus } from 'state/forms/models';
import {
  getBatchFormJsonRepresentation,
  getBatchFormStatus,
  getBatchFormValidationErrors,
  getBatchQuoteCreatedAt,
  getBatchQuoteEstimatedDurationInMilliseconds,
  getBatchQuotePricingStartDate,
  getBatchQuoteResultWarning,
  getBatchQuoteSolveResult,
  getBatchTradeInfos,
  getBatchTradeWay,
  getEffectiveExposedBatchFormStatus,
  getFormQuoteRejections,
  getIsUserInternal,
  isBatchFormLoading,
  isBatchFormSelected,
} from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

import type {
  BatchFormHeaderCellDispatchProps,
  BatchFormHeaderCellOwnProps,
  BatchFormHeaderCellStateProps,
} from './FormHeaderCell.models';

export const mapStateToProps: MapStateToProps<BatchFormHeaderCellStateProps, BatchFormHeaderCellOwnProps, AppState> = (
  state: AppState,
  ownProps: BatchFormHeaderCellOwnProps,
): BatchFormHeaderCellStateProps => {
  const formStatus = getBatchFormStatus(state, ownProps.formId);

  // TODO: Migrate these logic into a selector
  const formJsonRepresentation =
    ownProps.formId !== undefined && formStatus === 'REJECTED'
      ? getBatchFormJsonRepresentation(state, ownProps.formId)
      : undefined;

  return {
    exposedFormStatus: getEffectiveExposedBatchFormStatus(state, ownProps.formId),
    formStatus,
    isSelected: isBatchFormSelected(state, ownProps.formId),
    isLoading: ownProps.formId ? isBatchFormLoading(state, ownProps.formId) : true,
    pricingEstimatedDurationInMilliseconds: getBatchQuoteEstimatedDurationInMilliseconds(state, ownProps.formId),
    tradeAuthStartTimeInMillis: getBatchTradeInfos(state, ownProps.formId)?.tradeAuthStartTimeInMillis,
    tradeAuthDurationInMillis: getBatchTradeInfos(state, ownProps.formId)?.tradeAuthDurationInMillis,
    tradeWay: getBatchTradeWay(state, ownProps.formId),
    pricingStartDate: getBatchQuotePricingStartDate(state, ownProps.formId),
    quoteSolveResult: getBatchQuoteSolveResult(state, ownProps.formId),
    workflowErrors: getFormQuoteRejections(state, 'newBatch', ownProps.formId),
    productSubtype: ownProps.productSubtype,
    formJsonRepresentation,
    isInternal: getIsUserInternal(state),
    warnings: getBatchQuoteResultWarning(state, ownProps.formId),
    validationErrors: getBatchFormValidationErrors(state, ownProps.formId),
    quoteCreatedAt: getBatchQuoteCreatedAt(state, ownProps.formId),
  };
};

export const mapDispatchToProps: MapDispatchToPropsFunction<
  BatchFormHeaderCellDispatchProps,
  BatchFormHeaderCellOwnProps
> = (dispatch: ThunkDispatch, ownProps: BatchFormHeaderCellOwnProps): BatchFormHeaderCellDispatchProps => ({
  onCheckboxChanged(value: boolean) {
    if (!ownProps.formId) {
      return;
    }
    logAnalytics.trackEvent('Batch Workspace', value ? 'Select an item' : 'Deselect an item');
    dispatch(batchFormSelectToggled(ownProps.formId));
  },
  onFailedFormDeleted() {
    if (!ownProps.formId) {
      return;
    }
    dispatch(batchFormsDeletedThunk(ownProps.tabId, [ownProps.formId]));
  },
  onFailedFormRetried(formStatus: FormStatus) {
    if (!ownProps.formId) {
      return;
    }

    switch (formStatus) {
      case 'INITIALIZATION_ERROR':
        dispatch(
          defaultBatchFormRequestedThunk(
            ownProps.tabId,
            ownProps.productType,
            ownProps.productSubtype,
            ownProps.wrapper,
            ownProps.payOffType,
          ),
        );
        dispatch(batchFormsDeleteRequested([ownProps.formId]));
        dispatch(batchFormsDeleted(ownProps.tabId, [ownProps.formId]));
        break;
      case 'PATCH_ERROR':
        dispatch(batchFormRestoreSessionThunk(ownProps.tabId, ownProps.formId));
        break;
    }
  },
  onPricingTimeout() {
    if (!ownProps.formId) {
      return;
    }
    dispatch(batchQuotePushBackCancelRequested(ownProps.formId));
  },

  onQuoteExpired() {
    if (!ownProps.formId) {
      return;
    }
    dispatch(batchQuoteExpired(ownProps.formId));
  },
});

export const connectedBatchFormHeaderCell = connect<
  BatchFormHeaderCellStateProps,
  BatchFormHeaderCellDispatchProps,
  BatchFormHeaderCellOwnProps,
  AppState
>(mapStateToProps, mapDispatchToProps);
